import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import {
  InventoryLogsResponse,
  InventoryResponse,
  InventoryResponseModel,
  Pagination,
  RecordsList,
} from "../Helpers/Interfaces/InventoryType";
import { fileResponseModel } from "./ProductService";

export interface inventoryfileblockedres {
  fileId: any;
}

export const InventoryService = createApi({
  reducerPath: "InventoryService",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ""),
  tagTypes: ["Inventory"],
  endpoints: (builder) => ({
    validateInventoryFile: builder.mutation<
      InventoryResponseModel,
      { file: File }
    >({
      query: ({ file }) => {
        const formData: any = new FormData();
        formData.append("file", file);
        return {
          url: ApiPath.validateInventoryFile,
          method: "POST",
          body: formData,
          extraOptions: {
            fileUpload: true,
          },
        };
      },
    }),

    uploadInventoryFile: builder.mutation<fileResponseModel, any>({
      query: (reqBody) => {
        return {
          url: ApiPath.uploadInventoryFile,
          method: "POST",
          body: reqBody,
        };
      },
    }),

    inventoryfileblocked: builder.mutation<inventoryfileblockedres, any>({
      query: (reqBody) => {
        return {
          url: ApiPath.inventoryfileblocked,
          method: "POST",
          body: reqBody,
        };
      },
    }),

    tempUploadInventoryFile: builder.mutation<fileResponseModel, any>({
      query: (parmData) => {
        return {
          url: ApiPath.tempUploadInventoryFile,
          method: "POST",
          body: parmData,
          extraOptions: {
            fileUpload: true,
          },
        };
      },
    }),

    getInventoryList: builder.mutation<InventoryResponse, Pagination>({
      query: (params) => ({
        url: ApiPath.inventorylistinmeta,
        method: "POST",
        body: {
          filters: params.filters || [],
          // These were hardcoded to "desc" with sortBy omitted entirely, so the
          // column sorting on the Inventory List silently did nothing: the
          // screen passed the chosen column and direction, and this rebuilt the
          // body without them. Default stays newest-first when the caller sends
          // neither.
          sortOrder: params.sortOrder || "desc",
          ...(params.sortBy ? { sortBy: params.sortBy } : {}),
          page: params.page || 1,
          pageSize: params.pageSize || 10,
          search: params.search,
        },
      }),
    }),

    getInventoryListOfFile: builder.mutation<InventoryResponse, Pagination>({
      query: (params) => ({
        url: ApiPath.inventoryList,
        method: "POST",
        body: {
          filters: params.filters || [],
          sortOrder: "desc",
          page: params.page || 1,
          pageSize: params.pageSize || 10,
        },
      }),
    }),

    getinventoryfileemprecords: builder.mutation<any, any>({
      query: (params) => {
        return {
          url: ApiPath.inventoryfileemprecords,
          method: "POST",
          body: {
            fileId: params.fileId, // Include the id in the body
            sortBy: "uploadedAt",
            sortOrder: "desc",
            page: params.page || 1,
            pageSize: params.pageSize || 10,
          },
        };
      },
    }),

    getInventoryFileBlockedHistoryList: builder.mutation<any, any>({
      query: (payload) => ({
        url: ApiPath.inventoryfileblockedhistorylist,
        method: "POST",
        body: payload,
      }),
    }),

    getInventoryFileHistoryList: builder.mutation<InventoryLogsResponse, any>({
      query: (reqBody) => ({
        url: ApiPath.inventoryFileHistoryList,
        method: "POST",
        body: reqBody,
      }),
      transformResponse: (response: any): InventoryLogsResponse => {
        const {
          status,
          totalRecords,
          totalPages,
          currentPage,
          pageSize,
          data,
        } = response;

        const {
          validRecords,
          existingRecords,
          fileErrors,
          blockedRecords,
          ...baseData
        } = data ?? {};

        let allRecords: RecordsList = { pins: [] };
        let type = "";
        if (validRecords?.pins?.length) {
          allRecords = validRecords;
          type = "validRecords";
        } else if (existingRecords?.pins?.length) {
          allRecords = existingRecords;
          type = "existingRecords";
        } else if (Array.isArray(fileErrors) && fileErrors?.length) {
          type = "fileErrors";
          allRecords.pins = fileErrors;
        } else if (
          Array.isArray(blockedRecords?.pins) &&
          blockedRecords?.pins?.length
        ) {
          type = "blockedRecords";
          allRecords.pins = blockedRecords.pins;
        }

        return {
          status,
          totalRecords,
          totalPages,
          currentPage,
          pageSize,
          data: {
            _id: baseData._id,
            fileName: baseData.fileName,
            filePath: baseData.filePath,
            createdAt: baseData.createdAt || null,
            allRecords,
            type,
          },
        };
      },
    }),
  }),
});

export const {
  useValidateInventoryFileMutation,
  useUploadInventoryFileMutation,
  useTempUploadInventoryFileMutation,
  useGetInventoryListMutation,
  useGetInventoryFileHistoryListMutation,
  useInventoryfileblockedMutation,
  useGetinventoryfileemprecordsMutation,
  useGetInventoryFileBlockedHistoryListMutation,
  useGetInventoryListOfFileMutation,
} = InventoryService;
